import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import {
    Home, Calendar, CheckSquare, ClipboardList, LogOut, MessageSquare, Gift,
    Utensils, Timer, TrendingUp, FileText, Menu, X,
} from "lucide-react";

const NAV = [
    { to: "/client", label: "Today", icon: Home, end: true, testid: "cl-nav-home" },
    { to: "/client/calendar", label: "Schedule", icon: Calendar, testid: "cl-nav-calendar" },
    { to: "/client/messages", label: "Messages", icon: MessageSquare, testid: "cl-nav-messages", badgeKey: "msgs" },
    { to: "/client/documents", label: "Documents", icon: FileText, testid: "cl-nav-documents", badgeKey: "docs" },
    { to: "/client/habits", label: "Habits", icon: CheckSquare, testid: "cl-nav-habits" },
    { to: "/client/progress", label: "Progress", icon: TrendingUp, testid: "cl-nav-progress" },
    { to: "/client/nutrition", label: "Nutrition", icon: Utensils, testid: "cl-nav-nutrition" },
    { to: "/client/timer", label: "Timer", icon: Timer, testid: "cl-nav-timer" },
    { to: "/client/check-in", label: "Check-in", icon: ClipboardList, testid: "cl-nav-checkin" },
    { to: "/client/referrals", label: "Refer friends", icon: Gift, testid: "cl-nav-referrals" },
];

export default function ClientLayout({ children }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [badges, setBadges] = useState({ docs: 0, msgs: 0 });

    useEffect(() => {
        const loadBadges = async () => {
            try {
                const [d, m] = await Promise.all([
                    api.get("/me/documents/pending-count"),
                    api.get("/me/messages/unread"),
                ]);
                setBadges({ docs: d.data.count || 0, msgs: m.data.unread || 0 });
            } catch (_e) {}
        };
        loadBadges();
        const t = setInterval(loadBadges, 15000);
        return () => clearInterval(t);
    }, []);

    const [open, setOpen] = useState(() => {
        if (typeof window === "undefined") return true;
        const saved = localStorage.getItem("nde_client_sidebar_open");
        if (saved !== null) return saved === "1";
        return window.innerWidth >= 1024;
    });

    useEffect(() => {
        localStorage.setItem("nde_client_sidebar_open", open ? "1" : "0");
    }, [open]);

    const closeOnMobile = () => {
        if (typeof window !== "undefined" && window.innerWidth < 1024) setOpen(false);
    };

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] relative" data-testid="client-shell">
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="client-sidebar-toggle"
                    aria-label="Show sidebar"
                    className="fixed top-4 left-4 z-50 flex items-center justify-center w-10 h-10 bg-[var(--surface)] border border-[var(--border-bright)] text-[var(--text)] hover:text-[var(--gold)] hover:border-[var(--gold)] transition-all"
                >
                    <Menu size={16} />
                </button>
            )}

            {open && (
                <div
                    onClick={() => setOpen(false)}
                    className="fixed inset-0 bg-black/60 z-30 lg:hidden"
                    data-testid="client-sidebar-backdrop"
                />
            )}

            <aside
                className={`fixed top-0 left-0 h-screen w-64 border-r border-[var(--border)] bg-[var(--surface)] flex flex-col z-40 transform transition-transform duration-300 ease-out ${
                    open ? "translate-x-0" : "-translate-x-full"
                }`}
            >
                <div className="p-6 border-b border-[var(--border)] relative">
                    <Logo size="sm" />
                    <p className="label-tiny mt-4">/ Athlete portal</p>
                    <button
                        onClick={() => setOpen(false)}
                        data-testid="client-sidebar-close"
                        aria-label="Hide sidebar"
                        className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 text-[var(--text-muted)] hover:text-[var(--gold)] border border-transparent hover:border-[var(--gold-deep)]"
                    >
                        <X size={14} />
                    </button>
                </div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    {NAV.map((item) => (
                        <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid} onClick={closeOnMobile}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors ${
                                    isActive
                                        ? "border-[var(--gold)] bg-[var(--surface-hover)] text-[var(--text)]"
                                        : "border-transparent text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
                                }`}>
                            <item.icon size={18} />
                            <span className="text-[14px] font-medium tracking-[0.12em] uppercase flex-1">{item.label}</span>
                            {item.badgeKey && badges[item.badgeKey] > 0 && (
                                <span className="px-1.5 py-0.5 bg-[var(--danger)] text-white text-[10px] font-mono" data-testid={`cl-badge-${item.badgeKey}`}>
                                    {badges[item.badgeKey]}
                                </span>
                            )}
                        </NavLink>
                    ))}
                </nav>
                <div className="border-t border-[var(--border)] p-3">
                    <div className="px-3 py-2 mb-2">
                        <p className="label-tiny">Signed in</p>
                        <p className="text-sm font-medium truncate">{user?.name}</p>
                        <p className="text-xs text-[var(--text-muted)] truncate font-mono">{user?.email}</p>
                    </div>
                    <button onClick={async () => { await logout(); nav("/login"); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-[var(--text-muted)] hover:text-[var(--danger)] hover:bg-[var(--surface-hover)] text-sm" data-testid="client-logout-btn">
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </aside>

            <main
                className={`min-h-screen overflow-x-hidden transition-[padding] duration-300 ease-out ${
                    open ? "lg:pl-64" : "pl-0"
                }`}
            >
                <div className={open ? "" : "pt-16"}>{children}</div>
            </main>

            {/* Sticky bottom mobile action bar — hidden on lg+ */}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[var(--surface)] border-t border-[var(--border)] grid grid-cols-4 gap-px bg-[var(--border)]" data-testid="mobile-bottom-bar">
                {[
                    { to: "/client", icon: Home, label: "Today", end: true, testid: "mb-home" },
                    { to: "/client/calendar", icon: Calendar, label: "Schedule", testid: "mb-cal" },
                    { to: "/client/messages", icon: MessageSquare, label: "Inbox", testid: "mb-msg", badgeKey: "msgs" },
                    { to: "/client/check-in", icon: ClipboardList, label: "Check-in", testid: "mb-checkin" },
                ].map((item) => (
                    <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid}
                        className={({ isActive }) =>
                            `relative flex flex-col items-center justify-center gap-1 py-2.5 bg-[var(--surface)] ${
                                isActive ? "text-[var(--gold-bright)]" : "text-[var(--text-muted)]"
                            }`}>
                        <item.icon size={18} />
                        <span className="text-[10px] font-mono tracking-wider uppercase">{item.label}</span>
                        {item.badgeKey && badges[item.badgeKey] > 0 && (
                            <span className="absolute top-1 right-3 min-w-[16px] h-4 px-1 inline-flex items-center justify-center bg-[var(--danger)] text-white text-[9px] font-mono rounded-full">
                                {badges[item.badgeKey]}
                            </span>
                        )}
                    </NavLink>
                ))}
            </nav>
        </div>
    );
}
